import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { logger } from '@heidi/runtime/logger';
import { cn } from '@heidi/utils';
import * as React from 'react';

import {
  clampCreditUsagePercent,
  getCreditAllowanceUsagePercent,
  isComplimentaryCreditAllowance,
} from '../../../data/query/credit-allowance.utils';
import { useQueryCreditAllowance } from '../../../data/query/useQueryCreditAllowance';
import { CreditUsageIndicatorButton } from './credit-usage-indicator-button';
import { CreditUsagePopoverContent } from './credit-usage-popover-content';
import { CREDIT_USAGE_POPOVER_PRESETS } from './credit-usage-popover-presets';
import { resolveCreditUsageTriggerCountdown } from './resolve-credit-usage-trigger-countdown';
import type { CreditUsageIndicatorPopoverProps } from './types';

/**
 * Connected credit-usage popover with an indicator button — fetches allowance, renders button + panel.
 * Use dumb {@link CreditUsagePopoverContent} when the trigger or anchor is app-owned.
 *
 * Loading and unavailable allowance both render nothing; fetch errors are logged and also render null.
 */
export function CreditUsageIndicatorPopover({
  plan,
  hasUser,
  isOnClinicianMax = false,
  isHeidi2OptInEnabled = false,
  isMaxUpgradeEnabled = false,
  trialRemaining,
  upgradeSeat = 'max',
  triggerVariant = 'tile',
  upgradeCtaLabel,
  onUpgradeClick,
  onExtendTrialClick,
  enabled = true,
  isUpgradePending = false,
  side = 'bottom',
  align = 'end',
  sideOffset = 8,
  className,
  popoverClassName,
  renderTrigger,
  'data-testid': testId = 'credit-usage-indicator-popover',
  open: openProp,
  onOpenChange,
  onDismissClick,
  learnMoreHref,
}: CreditUsageIndicatorPopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const upgradeButtonRef = React.useRef<HTMLButtonElement>(null);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const openedViaHandlerRef = React.useRef(false);
  const skipDismissClickRef = React.useRef(false);
  const prevControlledOpenRef = React.useRef(openProp);
  const {
    data: allowance,
    isPending,
    isError,
    error,
  } = useQueryCreditAllowance({
    hasUser,
    enabled,
  });

  React.useEffect(() => {
    if (isError) {
      logger.error('credit allowance fetch failed', error);
    }
  }, [isError, error]);

  const usagePercent =
    allowance != null
      ? clampCreditUsagePercent(getCreditAllowanceUsagePercent(allowance))
      : 0;

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        openedViaHandlerRef.current = true;
      } else if (!skipDismissClickRef.current) {
        onDismissClick?.();
      }
      skipDismissClickRef.current = false;

      if (!isControlled) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(
        nextOpen,
        nextOpen && allowance != null ? { usagePercent } : undefined
      );
    },
    [allowance, isControlled, onDismissClick, onOpenChange, usagePercent]
  );

  React.useEffect(() => {
    if (!isControlled || openProp === undefined) {
      return;
    }

    const wasOpen = prevControlledOpenRef.current ?? false;
    if (openProp && !wasOpen && allowance != null) {
      if (!openedViaHandlerRef.current) {
        onOpenChange?.(true, { usagePercent });
      }
      openedViaHandlerRef.current = false;
    }

    prevControlledOpenRef.current = openProp;
  }, [allowance, isControlled, onOpenChange, openProp, usagePercent]);

  if (!enabled || isPending || isError || allowance == null) {
    return null;
  }

  // Complimentary cycles still pass isComplimentaryUsage so the panel can swap
  // to Unlimited once display_limit is exhausted; trial countdown still wins
  // the status banner.
  const isComplimentaryUsage =
    isHeidi2OptInEnabled && isComplimentaryCreditAllowance(allowance);
  const showUpgradeCta =
    onUpgradeClick != null && (upgradeSeat === 'standard' || isMaxUpgradeEnabled);

  const handleUpgradeClick = () => {
    skipDismissClickRef.current = true;
    onUpgradeClick?.();
    handleOpenChange(false);
  };

  const handleExtendTrialClick = () => {
    skipDismissClickRef.current = true;
    onExtendTrialClick?.();
    handleOpenChange(false);
  };

  const buttonTestId = `${testId}-button`;
  const countdownValue = resolveCreditUsageTriggerCountdown(trialRemaining);
  const { theme } = CREDIT_USAGE_POPOVER_PRESETS[plan];
  const triggerButtonClass =
    triggerVariant !== 'plain' && 'triggerButtonClass' in theme
      ? theme.triggerButtonClass
      : undefined;
  const trigger = renderTrigger ? (
    renderTrigger({
      plan,
      usagePercent,
      triggerButtonClass,
      triggerVariant,
      countdownValue,
      'data-testid': buttonTestId,
    })
  ) : (
    <CreditUsageIndicatorButton
      plan={plan}
      usagePercent={usagePercent}
      triggerVariant={triggerVariant}
      countdownValue={countdownValue}
      className={className}
      data-testid={buttonTestId}
    />
  );

  const panel = (
    <CreditUsagePopoverContent
      plan={plan}
      usagePercent={usagePercent}
      cycleEnd={allowance.cycle_end}
      isOnClinicianMax={isOnClinicianMax}
      isComplimentaryUsage={isComplimentaryUsage}
      isHeidi2OptInEnabled={isHeidi2OptInEnabled}
      isMaxUpgradeEnabled={isMaxUpgradeEnabled}
      trialRemaining={trialRemaining}
      upgradeSeat={upgradeSeat}
      upgradeCtaLabel={upgradeCtaLabel}
      onUpgradeClick={showUpgradeCta ? handleUpgradeClick : undefined}
      isUpgradePending={isUpgradePending}
      onExtendTrialClick={onExtendTrialClick ? handleExtendTrialClick : undefined}
      upgradeButtonRef={upgradeButtonRef}
      learnMoreHref={learnMoreHref}
      data-testid={testId}
    />
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn('w-auto p-0', popoverClassName)}
        data-testid={`${testId}-content`}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          upgradeButtonRef.current?.focus();
        }}
      >
        {panel}
      </PopoverContent>
    </Popover>
  );
}
